import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../utils/translations';
import { authAPI } from '../../services/api';
import { ButtonLoader } from '../loading';
import '../PropertyFormModal/PropertyFormModal.css';
import './ChangePasswordModal.css';

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setError('');
    setSuccess('');
    setSubmitting(false);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formData.newPassword.length < 6) {
      setError(t('admin.agentForm.passwordTooShort') || 'Password must be at least 6 characters');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('admin.agentForm.passwordMismatch') || 'Passwords do not match');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await authAPI.changePassword(formData.currentPassword, formData.newPassword);
      setSuccess(t('admin.dashboard.passwordChanged') || 'Password changed successfully');
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (submitError) {
      setError(submitError.message || t('admin.dashboard.passwordChangeError') || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="modal-overlay change-password-overlay" onClick={onClose}>
      <div
        className="modal-content change-password-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
      >
        <div className="modal-header">
          <h2 id="change-password-title">
            {t('admin.dashboard.changePassword') || 'Change Password'}
          </h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <form className="property-form change-password-form" onSubmit={handleSubmit}>
          {error && <p className="change-password-error">{error}</p>}
          {success && <p className="change-password-success">{success}</p>}

          <div className="form-group">
            <label htmlFor="currentPassword">
              {t('admin.dashboard.currentPassword') || 'Current password'}
            </label>
            <input
              type="password"
              id="currentPassword"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              required
              autoComplete="current-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">
              {t('admin.dashboard.newPassword') || 'New password'}
            </label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              required
              minLength={6}
              autoComplete="new-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">
              {t('admin.dashboard.confirmPassword') || 'Confirm password'}
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              minLength={6}
              autoComplete="new-password"
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              {t('admin.form.cancel') || 'Cancel'}
            </button>
            <button type="submit" className="btn-save" disabled={submitting}>
              {submitting ? <ButtonLoader /> : (t('admin.dashboard.savePassword') || 'Save password')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ChangePasswordModal;
